import type { AnchoredRow } from '../types/anchoredRowTypes'
import type { CompleteMicroSegmentationResult, MicroUnit } from '../types/segmentationTypes'
import {
  buildLedgerSafeSegmentableWordSpans,
  createLedgerSafeUnitSplitContext,
  type LedgerSafeUnitSplitContext,
  type LedgerSafeUnitSplitWord
} from './ledgerSafeBoundaryContext'
import {
  getOrderedUnitUuid,
  getOrderedWitnessUnitStorage,
  getOrderedWitnessUnits,
  resolveLedgerSafeSegmentationSourceText,
  type OrderedWitnessUnit
} from './ledgerSafeBoundarySource'
import {
  cloneLedgerSafeSegmentationEnvelope,
  getLedgerSafeStorageContainerStart,
  getLedgerSafeStoredUnit
} from './ledgerSafeWitnessUnitStream'

export interface LedgerSafeUnitSplitPreview {
  context: LedgerSafeUnitSplitContext
  splitBoundaryWordIndex: number
  currentText: string
  newFragmentText: string
  currentStart: number
  currentEnd: number
  newFragmentStart: number
  newFragmentEnd: number
}

export interface LedgerSafeUnitSplitSegmentationApplyResult {
  updatedSegmentationResult: CompleteMicroSegmentationResult
  preview: LedgerSafeUnitSplitPreview
  changedUnitUuids: string[]
  createdUnitUuid: string
}

const buildSplitPreviewTextsFromWords = (
  words: LedgerSafeUnitSplitWord[],
  splitBoundaryWordIndex: number
): { currentText: string; newFragmentText: string } => {
  const leftTokens: string[] = []
  const rightTokens: string[] = []
  let boundaryCrossed = false

  words.forEach(word => {
    if (!boundaryCrossed && word.splitBoundaryWordIndex === splitBoundaryWordIndex) {
      leftTokens.push(word.text)
      boundaryCrossed = true
      return
    }
    if (boundaryCrossed) {
      rightTokens.push(word.text)
      return
    }
    leftTokens.push(word.text)
  })

  return {
    currentText: leftTokens.join(' ').trim(),
    newFragmentText: rightTokens.join(' ').trim()
  }
}

const findOrderedUnitByUuid = (
  orderedUnits: OrderedWitnessUnit[],
  unitUuid: string
): OrderedWitnessUnit | undefined =>
  orderedUnits.find(entry => getOrderedUnitUuid(entry.unit) === unitUuid)

export const generateLedgerSafeSplitUnitUuid = (reference: string): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  const timestamp = Date.now().toString(36)
  const entropy = Math.random().toString(36).slice(2, 10)
  return `${reference || 'unit'}__split_${timestamp}_${entropy}`
}

export const previewLedgerSafeUnitSplit = ({
  context,
  sourceText,
  splitBoundaryWordIndex,
  segmentableWordSpans
}: {
  context: LedgerSafeUnitSplitContext
  sourceText?: string
  splitBoundaryWordIndex: number
  segmentableWordSpans?: Array<{ start: number; end: number }>
}): LedgerSafeUnitSplitPreview => {
  const previewSourceText = context.sourceText || sourceText || ''
  const effectiveSegmentableWordSpans = segmentableWordSpans || buildLedgerSafeSegmentableWordSpans(previewSourceText)
  const clampedBoundaryWordIndex = Math.max(
    context.minSplitBoundaryWordIndex,
    Math.min(context.maxSplitBoundaryWordIndex, splitBoundaryWordIndex)
  )
  const leftEndSpan = effectiveSegmentableWordSpans[clampedBoundaryWordIndex - 1]
  const rightStartSpan = effectiveSegmentableWordSpans[clampedBoundaryWordIndex]
  if (!leftEndSpan || !rightStartSpan) {
    throw new Error('Unable to preview split outside segmentable range')
  }

  const previewTexts = buildSplitPreviewTextsFromWords(context.words, clampedBoundaryWordIndex)

  return {
    context,
    splitBoundaryWordIndex: clampedBoundaryWordIndex,
    currentText: previewTexts.currentText,
    newFragmentText: previewTexts.newFragmentText,
    currentStart: context.unitStart,
    currentEnd: leftEndSpan.end,
    newFragmentStart: rightStartSpan.start,
    newFragmentEnd: context.unitEnd
  }
}

export const applyLedgerSafeUnitSplitToSegmentation = ({
  rows,
  witnessId,
  rowId,
  segmentationResult,
  sourceText,
  splitBoundaryWordIndex
}: {
  rows: AnchoredRow[]
  witnessId: string
  rowId: string
  segmentationResult: CompleteMicroSegmentationResult | null | undefined
  sourceText: string
  splitBoundaryWordIndex: number
}): LedgerSafeUnitSplitSegmentationApplyResult => {
  const context = createLedgerSafeUnitSplitContext({
    rows,
    witnessId,
    rowId,
    segmentationResult,
    sourceText
  })
  if (!context || !segmentationResult) {
    throw new Error('Selected unit cannot be split')
  }

  const effectiveSourceText = resolveLedgerSafeSegmentationSourceText({
    segmentationResult,
    fallbackSourceText: sourceText
  })
  const preview = previewLedgerSafeUnitSplit({
    context,
    sourceText: effectiveSourceText,
    splitBoundaryWordIndex
  })
  if (!preview.currentText.trim() || !preview.newFragmentText.trim()) {
    throw new Error('Split must leave text on both sides of the boundary')
  }

  const updatedSegmentationResult = cloneLedgerSafeSegmentationEnvelope(segmentationResult)
  const orderedUnits = getOrderedWitnessUnits(updatedSegmentationResult)
  const unitEntry = findOrderedUnitByUuid(orderedUnits, context.unitUuid)
  if (!unitEntry) {
    throw new Error('Unable to locate unit in segmentation result')
  }

  const storage = getOrderedWitnessUnitStorage(unitEntry)
  const targetChunk = updatedSegmentationResult.chunk_results?.[storage.containerIndex]
  const targetUnit = getLedgerSafeStoredUnit(updatedSegmentationResult, storage)
  if (!targetChunk || !targetUnit) {
    throw new Error('Unable to locate split target in chunk')
  }

  const chunkStart = getLedgerSafeStorageContainerStart(updatedSegmentationResult, storage)
  const createdUnitUuid = generateLedgerSafeSplitUnitUuid(context.unitUuid)
  const createdUnitSuffix = createdUnitUuid.slice(-8)
  const createdUnitIdBase = String(targetUnit.unit_id || context.unitUuid || 'unit')
  const createdUnitLabelBase = String(targetUnit.unit_label || targetUnit.unit_id || context.unitLabel || 'Unit')

  targetUnit.start_position = preview.currentStart
  targetUnit.end_position = preview.currentEnd
  targetUnit.chunk_relative_start = preview.currentStart - chunkStart
  targetUnit.chunk_relative_end = preview.currentEnd - chunkStart
  targetUnit.unit_length_chars = preview.currentEnd - preview.currentStart
  targetUnit.unit_text_content = preview.currentText

  const createdUnit: MicroUnit = {
    ...targetUnit,
    unit_id: `${createdUnitIdBase}__split_${createdUnitSuffix}`,
    unit_uuid: createdUnitUuid,
    unit_label: `${createdUnitLabelBase}b`,
    unit_number: typeof targetUnit.unit_number === 'number' ? targetUnit.unit_number + 0.5 : targetUnit.unit_number,
    start_position: preview.newFragmentStart,
    end_position: preview.newFragmentEnd,
    chunk_relative_start: preview.newFragmentStart - chunkStart,
    chunk_relative_end: preview.newFragmentEnd - chunkStart,
    unit_length_chars: preview.newFragmentEnd - preview.newFragmentStart,
    unit_text_content: preview.newFragmentText,
    processing_metadata: {
      ...(targetUnit.processing_metadata || {}),
      ledger_safe_split_from: context.unitUuid
    }
  }

  targetChunk.micro_units.splice(storage.unitIndex + 1, 0, createdUnit)
  updatedSegmentationResult.segmentation_metadata = {
    ...(updatedSegmentationResult.segmentation_metadata || {}),
    timestamp: new Date().toISOString()
  }

  return {
    updatedSegmentationResult,
    preview,
    changedUnitUuids: [context.unitUuid, createdUnitUuid],
    createdUnitUuid
  }
}
